import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { VehicleService, UpdateVehiclePayload } from '../../services/vehicle.service';
import { VehicleData } from '../../shared/types';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { sanitizeErrorMessage } from '../../shared/utils/error.util';
import { runFetchVehicleData } from '../../shared/utils/vehicle-fetch.util';
import { Observable, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AddVehicleFormComponent } from '../add-vehicle-form/add-vehicle-form.component';
import { US_STATES, State } from '../../shared/constants/us-states.constant';

export interface VehicleListData {
  selectedState: string;
  licensePlate: string;
  vehicleData: VehicleData | null;
  isVehicleFetched: boolean;
  vehicleId?: string; // Will be set after vehicle creation
}

export interface EditVehicleData {
  id: string;
  licensePlate: string;
  state: string;
  vehicleData: VehicleData;
}

/**
 * VehicleListComponent - Displays and manages a single vehicle entry within a vehicle list (fetches vehicle details by license/state, supports edit/add flows and manual creation).
 * @component
 * @selector app-vehicle-list
 * @example
 *   <app-vehicle-list
 *     [initialData]="sampleInitialData"
 *     [isEditMode]="true"
 *     [editVehicleData]="vehicleToEdit"
 *     [customerId]="'cust-123'"
 *     (formDataChange)="onFormDataChange($event)"
 *     (formValidityChange)="onValidityChange($event)"
 *     (saveEdit)="onSaveEdit($event)"
 *     (saveNewVehicle)="onSaveNew($event)"
 *   ></app-vehicle-list>
 * @input {VehicleListData | undefined} initialData - Optional initial form data used to pre-populate license, state and fetched vehicle details.
 * @input {boolean} isEditMode - When true the component operates in edit mode (saves updates to an existing vehicle); otherwise used to add a new vehicle.
 * @input {EditVehicleData | undefined} editVehicleData - Edit-mode payload containing id, license plate, state and existing vehicleData to initialize the component.
 * @input {boolean} fromAppointment - When true indicates the vehicle context is coming from an appointment (affects initialization behavior).
 * @input {boolean} isBookingChecked - Initial checked state for any booking checkbox shown in the row.
 * @input {boolean} isBookingDisabled - When true booking checkbox is disabled.
 * @input {boolean} isRadioButton - When true renders selection as radio button style instead of default.
 * @input {boolean} showSaveCancelButtons - Controls visibility of save/cancel buttons for edit/add actions.
 * @input {boolean} showBookingCheckbox - Controls whether the booking checkbox is shown (typically only shown when multiple vehicles present).
 * @input {string} customerId - Optional customer ID used when creating a vehicle manually for a specific customer.
 * @output {EventEmitter<VehicleListData>} formDataChange - Emits the current form state (selectedState, licensePlate, vehicleData, isVehicleFetched, vehicleId) whenever it changes.
 * @output {EventEmitter<boolean>} formValidityChange - Emits whether the current form is considered valid (vehicle fetched and data present).
 * @output {EventEmitter<void>} cancelEdit - Emitted when the user cancels an edit action.
 * @output {EventEmitter<EditVehicleData>} saveEdit - Emitted with updated EditVehicleData when saving changes in edit mode.
 * @output {EventEmitter<{licensePlate: string, state: string}>} checkDuplicate - Emitted to request duplicate checking for the current license/state pair.
 * @output {EventEmitter<boolean>} bookingChange - Emitted when the booking checkbox changes state.
 * @output {EventEmitter<VehicleListData>} saveNewVehicle - Emitted with VehicleListData when saving a newly added vehicle (non-edit mode).
 * @output {EventEmitter<any>} vehicleCreatedManually - Emitted when a vehicle is created manually (from the manual form) and includes created vehicle payload.
 * @output {EventEmitter<boolean>} manualFieldsVisibilityChange - Emitted when the visibility of manual input fields changes (pass-through).
 *
 * Key methods:
 *  - initializeFromData(): initialize internal state from initialData or editVehicleData depending on mode.
 *  - createVehicleForCustomer(customerId: string): Observable<any> - Creates a vehicle for the given customer using the current vehicleData and emits updated form data on success.
 *  - onInputChange(): resets fetched status and vehicle data when user edits inputs and emits form state.
 *  - onFetchVehicle(): orchestrates fetching vehicle details by license/state, updates loading/error states, emits duplicate-check and form state.
 *  - emitFormData(): constructs and emits the VehicleListData and current validity via formDataChange and formValidityChange.
 *  - onCancelEdit(), onSaveEdit(): handlers for cancel/save actions (save behavior differs for edit vs add mode).
 *  - onVehicleFormChange(), onVehicleDataChange(), onVehicleCreatedManually(): handlers for child add-vehicle-form events to update internal state and propagate changes.
 *  - onManualFieldsVisibilityChange(): forwards manual field visibility changes to parent.
 *  - Getters: canFetch (whether license/state are present), isValid (whether a vehicle has been fetched), canSave (whether current state can be saved).
 *
 * Lifecycle hooks:
 *  - ngOnInit(): Initializes component state from inputs and emits initial form data.
 *  - ngOnChanges(): Re-initializes state when input bindings change and re-emits form data.
 */
@Component({
  selector: 'app-vehicle-list',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSelectModule, AddVehicleFormComponent],
  templateUrl: './vehicle-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./vehicle-list.component.scss'],
})
export class VehicleListComponent implements OnChanges, OnInit {
  @Input() initialData?: VehicleListData;
  @Input() isEditMode = false;
  @Input() editVehicleData?: EditVehicleData;
  @Input() fromAppointment = false;
  @Input() isBookingChecked = true;
  @Input() isBookingDisabled = false;
  @Input() isRadioButton = false;
  @Input() showSaveCancelButtons = true;
  @Input() showBookingCheckbox = true; // Show booking checkbox only when there are multiple vehicles
  @Input() customerId = ''; // Customer ID for creating vehicle manually
  @Output() formDataChange = new EventEmitter<VehicleListData>();
  @Output() formValidityChange = new EventEmitter<boolean>();
  @Output() cancelEdit = new EventEmitter<void>();
  @Output() saveEdit = new EventEmitter<EditVehicleData>();
  @Output() checkDuplicate = new EventEmitter<{ licensePlate: string; state: string }>();
  @Output() bookingChange = new EventEmitter<boolean>();
  @Output() saveNewVehicle = new EventEmitter<VehicleListData>();
  @Output() vehicleCreatedManually = new EventEmitter<any>(); // Emit when vehicle is created manually
  @Output() manualFieldsVisibilityChange = new EventEmitter<boolean>(); // Emit when manual fields visibility changes

  selectedState = '';
  licensePlate = '';

  isVehicleFetched = false;
  isLoading = false;
  errorMessage = '';
  vehicleId = '';

  // Vehicle data from API
  vehicleData: VehicleData | null = null;

  constructor(
    private vehicleService: VehicleService,
    private messageService: NotificationService
  ) {
    this.initializeFromData();
  }

  ngOnInit(): void {
    this.initializeFromData();
    this.emitFormData();
  }

  ngOnChanges(): void {
    this.initializeFromData();
    this.emitFormData();
  }

  /**
   * Initialize component properties from stored edit or initial data, preferring editVehicleData when in edit mode.
   * @example
   * this.initializeFromData()
   * // No return value; component properties (selectedState, licensePlate, vehicleData, isVehicleFetched, vehicleId) are populated/updated.
   * @returns {void} No return value; updates instance properties based on editVehicleData or initialData.
   */
  private initializeFromData(): void {
    if (this.isEditMode && this.editVehicleData) {
      // Initialize with edit data
      this.selectedState = this.editVehicleData.state || '';
      this.licensePlate = this.editVehicleData.licensePlate || '';
      if (!this.fromAppointment) {
        this.vehicleData = this.editVehicleData.vehicleData || null;
        this.isVehicleFetched = true;
        this.vehicleId = this.editVehicleData.id || '';
      }
    } else if (this.initialData) {
      // Initialize with initial data
      this.selectedState = this.initialData.selectedState || '';
      this.licensePlate = this.initialData.licensePlate || '';
      this.vehicleData = this.initialData.vehicleData || null;
      this.isVehicleFetched = this.initialData.isVehicleFetched || false;
      this.vehicleId = this.initialData.vehicleId || '';
    }
  }

  // US States data
  states: State[] = US_STATES;

  get canFetch(): boolean {
    return this.selectedState.trim() !== '' && this.licensePlate.trim() !== '';
  }

  get isValid(): boolean {
    return this.isVehicleFetched && this.vehicleData !== null;
  }

  /**
   * Create a vehicle for the given customer using local vehicle data and return the creation Observable; sets this.vehicleId and emits form data on success.
   * @example
   * createVehicleForCustomer('cust123')
   * Observable<{ status: true, data: { id: 'veh456' } }>
   * @param {{string}} {{customerId}} - Customer ID for whom to create the vehicle.
   * @returns {{Observable<any>}} Observable that resolves to the vehicle creation response or emits an error if required data is missing.
   **/
  public createVehicleForCustomer(customerId: string): Observable<any> {
    if (!this.vehicleData || !customerId) {
      return throwError(() => new Error('Vehicle data or customer ID missing'));
    }

    let yearNum: number =
      typeof this.vehicleData.year === 'string'
        ? parseInt(this.vehicleData.year, 10)
        : this.vehicleData.year || 0;
    if (!Number.isFinite(yearNum)) {
      yearNum = 0;
    }

    const existingId = (this.vehicleData.id || '').toString().trim();
    if (existingId) {
      const updatePayload: UpdateVehiclePayload = {
        license_plate: this.licensePlate.trim(),
        state: this.selectedState,
        vin: this.vehicleData.vin || '',
        make: this.vehicleData.make || '',
        model: this.vehicleData.model || '',
        year: yearNum,
        customer_id: customerId,
        is_family_plan: false,
      };
      return this.vehicleService.updateVehicle(existingId, updatePayload).pipe(
        tap(response => {
          if (response?.status) {
            this.vehicleId = existingId;
            this.emitFormData();
          }
        })
      );
    }

    const createVehiclePayload = {
      license_plate: this.licensePlate.trim(),
      state: this.selectedState,
      vin: this.vehicleData.vin,
      make: this.vehicleData.make,
      model: this.vehicleData.model,
      year: this.vehicleData.year,
      customer_id: customerId,
      is_family_plan: false,
    };

    return this.vehicleService.createVehicle(createVehiclePayload).pipe(
      tap(response => {
        if (response.status && response.data?.id) {
          this.vehicleId = response.data.id;
          this.emitFormData();
        }
      })
    );
  }

  onInputChange(): void {
    // Reset vehicle fetched status when input changes
    this.isVehicleFetched = false;
    this.vehicleData = null;
    this.errorMessage = '';
    this.emitFormData();
  }

  /**
   * Attempts to fetch vehicle data for the current license plate and selected state; updates component state, emits events, and handles success/failure/error scenarios.
   * @example
   * nFetchVehicle()
   * undefined
   * @returns {void} Does not return a value; updates component state and emits events based on fetch results.
   **/
  onFetchVehicle(): void {
    if (!this.canFetch || this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.vehicleData = null;

    runFetchVehicleData(
      this.vehicleService,
      {
        license_plate: this.licensePlate.trim(),
        state: this.selectedState,
        // Read-only: Save writes the vehicle, once, with the customer on it (CWS-1398).
        persist: false,
      },
      {
        onSuccess: data => {
          this.isLoading = false;
          this.vehicleData = data;
          this.isVehicleFetched = true;
          this.errorMessage = '';
          this.checkDuplicate.emit({
            licensePlate: this.licensePlate.trim(),
            state: this.selectedState,
          });
          this.emitFormData();
        },
        onFailure: message => {
          this.isLoading = false;
          this.errorMessage = message;
          this.isVehicleFetched = false;
          this.emitFormData();
        },
        onError: message => {
          this.isLoading = false;
          this.errorMessage = message;
          this.isVehicleFetched = false;
          this.vehicleData = null;
          this.emitFormData();
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: sanitizeErrorMessage(message),
          });
        },
      }
    );
  }

  /**
   * Emit current vehicle list form data and validity through the component's EventEmitters.
   * @example
   * emitFormData()
   * undefined
   * @returns {{void}} Emits formDataChange with VehicleListData and formValidityChange with a boolean; does not return a value.
   **/
  private emitFormData(): void {
    const formData: VehicleListData = {
      selectedState: this.selectedState,
      licensePlate: this.licensePlate,
      vehicleData: this.vehicleData,
      isVehicleFetched: this.isVehicleFetched,
      vehicleId: this.vehicleId,
    };

    this.formDataChange.emit(formData);
    this.formValidityChange.emit(this.isValid);
  }

  // Edit mode methods
  onCancelEdit(): void {
    this.cancelEdit.emit();
  }

  /**
   * Save the current vehicle form: emits an edit event with EditVehicleData if in edit mode, otherwise emits a new-vehicle event with VehicleListData.
   * @example
   * nSaveEdit()
   * undefined
   * @returns {void} Emits events to the parent component; does not return a value.
   **/
  onSaveEdit(): void {
    if (!this.vehicleData) {
      return;
    }

    if (this.isEditMode && this.editVehicleData) {
      // Edit mode - save existing vehicle
      const updatedData: EditVehicleData = {
        id: this.editVehicleData.id,
        licensePlate: this.licensePlate.trim(),
        state: this.selectedState,
        vehicleData: this.vehicleData,
      };
      this.saveEdit.emit(updatedData);
    } else {
      // Add mode - emit form data for parent to handle
      const formData: VehicleListData = {
        selectedState: this.selectedState,
        licensePlate: this.licensePlate,
        vehicleData: this.vehicleData,
        isVehicleFetched: this.isVehicleFetched,
        vehicleId: this.vehicleId,
      };
      this.saveNewVehicle.emit(formData);
    }
  }

  get canSave(): boolean {
    return (
      this.isVehicleFetched &&
      this.vehicleData !== null &&
      this.selectedState.trim() !== '' &&
      this.licensePlate.trim() !== ''
    );
  }

  // Event handlers for add-vehicle-form component
  onVehicleFormChange(formData: { state: string; licensePlate: string }): void {
    this.selectedState = formData.state;
    this.licensePlate = formData.licensePlate;
    this.onInputChange();
  }

  /**
   * Updates the component with new vehicle data, handles clearing state, triggers duplicate check, and emits the updated form data.
   * @example
   * nVehicleDataChange({ licensePlate: 'ABC123', state: 'CA' })
   * undefined
   * @param {{VehicleData|null}} {{vehicleData}} - The vehicle data to set, or null to clear the current vehicle.
   * @returns {{void}} No return value; updates internal state and emits events.
   **/
  onVehicleDataChange(vehicleData: VehicleData | null): void {
    this.vehicleData = vehicleData;
    if (vehicleData) {
      this.isVehicleFetched = true;
      this.errorMessage = '';

      // Check for duplicates before emitting form data
      this.checkDuplicate.emit({
        licensePlate: this.licensePlate.trim(),
        state: this.selectedState,
      });
    } else {
      this.isVehicleFetched = false;
    }
    this.emitFormData();
  }

  onVehicleErrorMessageChange(errorMessage: string): void {
    this.errorMessage = errorMessage;
  }

  onIsLoadingVehicleChange(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  onBookingChange(isChecked: boolean): void {
    this.bookingChange.emit(isChecked);
  }

  onVehicleCreatedManually(vehicleData: any): void {
    // Update local vehicle data
    this.vehicleData = vehicleData;
    this.isVehicleFetched = true;
    this.errorMessage = '';
    this.vehicleId = vehicleData.id || '';

    // Emit to parent
    this.vehicleCreatedManually.emit(vehicleData);
    this.emitFormData();
  }

  onManualFieldsVisibilityChange(isVisible: boolean): void {
    // Pass through to parent
    this.manualFieldsVisibilityChange.emit(isVisible);
  }
}
